import React from 'react';
import { Check, RefreshCw, X, Loader } from 'lucide-react';

/**
 * Toast Notification Component
 * Shows non-blocking status updates for webhook submissions
 * 
 * @param {Object} props
 * @param {string} props.status - 'pending', 'success', 'failed'
 * @param {string} props.message - Main message to display
 * @param {Function} props.onRetry - Callback when retry button is clicked
 * @param {Function} props.onDismiss - Callback when dismiss button is clicked
 * @param {boolean} props.showRetry - Whether to show retry button (default: true when failed)
 */
const ToastNotification = ({
  status = 'pending',
  message = '',
  onRetry = null,
  onDismiss = null,
  showRetry = true
}) => {
  // Don't render if no status
  if (!status) return null;

  const getStatusConfig = () => {
    switch (status) {
      case 'pending':
      case 'attempting':
      case 'retrying':
        return {
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-800',
          icon: <Loader className="w-5 h-5 text-blue-600 animate-spin" />,
          defaultMessage: 'جاري حفظ بياناتك...'
        };
      case 'success':
        return {
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-800',
          icon: <Check className="w-5 h-5 text-green-600" />,
          defaultMessage: 'تم حفظ البيانات بنجاح ✅'
        };
      case 'failed':
        return {
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
          icon: <X className="w-5 h-5 text-red-600" />,
          defaultMessage: 'فشل الحفظ، يرجى المحاولة مرة أخرى'
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  const displayMessage = message || config.defaultMessage;

  return (
    <div
      className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in"
      translate="no"
      style={{
        animation: 'slideUp 0.3s ease-out'
      }}
    >
      <div
        className={`flex items-center gap-3 ${config.bgColor} ${config.borderColor} border-2 rounded-xl px-5 py-4 shadow-lg max-w-md min-w-[320px]`}
        dir="rtl"
      >
        {/* Icon */}
        <div className="flex-shrink-0">
          {config.icon}
        </div>

        {/* Message */}
        <div className={`flex-1 ${config.textColor} font-medium text-sm`}>
          {displayMessage}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Retry button - only show on failed status and if callback provided */}
          {status === 'failed' && showRetry && onRetry && (
            <button
              onClick={onRetry}
              className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 hover:scale-105"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>إعادة المحاولة</span>
            </button>
          )}

          {/* Dismiss button - show on success or if onDismiss provided */}
          {(status === 'success' || onDismiss) && (
            <button
              onClick={onDismiss}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="إغلاق"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ToastNotification;

